import React from 'react';

const StatisticsPanel = ({ stats }) => {
  const getDepartmentIcon = (department) => {
    const icons = {
      '中文科': '📝',
      '英文科': '🔤',
      '數學科': '🔢',
      '化學科': '🧪',
      '物理科': '⚛️',
      '生物科': '🧬',
      '歷史科': '📜',
      '地理科': '🌍',
      '經濟科': '💰',
      '音樂科': '🎵',
      '體育科': '⚽',
      '視覺藝術科': '🎨',
      '資訊科技科': '💻',
      '通識教育科': '📚',
      '學生支援組': '🤝'
    };
    return icons[department] || '📋';
  };

  const getLeaveTypeIcon = (leaveType) => {
    const icons = {
      '進修培訓': '📚',
      '公幹': '✈️',
      '事假': '🏠',
      '病假': '🏥',
      '會議': '👥'
    };
    return icons[leaveType] || '📋';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': '💾',
      'pending': '⏳',
      'approved': '✅'
    };
    return icons[status] || '📋';
  };

  const getMonthlyRecordData = () => {
    const monthlyData = stats.monthlyRecordCount || {};
    const months = [
      '一月', '二月', '三月', '四月', '五月', '六月',
      '七月', '八月', '九月', '十月', '十一月', '十二月'
    ];
    
    return months.map((month, index) => {
      const monthKey = `${new Date().getFullYear()}-${(index + 1).toString().padStart(2, '0')}`;
      return {
        month,
        count: monthlyData[monthKey] || 0
      };
    });
  };

  const monthlyData = getMonthlyRecordData();
  const maxMonthlyCount = Math.max(...monthlyData.map(d => d.count), 1);

  return (
    <div className="statistics-panel">
      <div className="stats-header">
        <h3>📊 SDR 統計概覽</h3>
        <p>教師請假/進修/公幹記錄系統整體數據</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👨‍🏫</div>
          <div className="stat-content">
            <h4>教師統計</h4>
            <div className="stat-number">{stats.totalTeachers}</div>
            <p>總教師數</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h4>學生統計</h4>
            <div className="stat-number">{stats.totalStudents}</div>
            <p>總學生數</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h4>記錄統計</h4>
            <div className="stat-number">{stats.totalRecords}</div>
            <p>總記錄數</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h4>影響統計</h4>
            <div className="stat-number">{stats.totalAffectedStudents}</div>
            <p>總影響學生數</p>
          </div>
        </div>
      </div>

      <div className="detailed-stats">
        <div className="status-distribution">
          <h4>記錄狀態分布</h4>
          <div className="status-stats">
            {Object.entries(stats.statusStats || {}).map(([status, count]) => (
              <div key={status} className="status-stat-item">
                <div className="status-info">
                  <span className="status-icon">{getStatusIcon(status)}</span>
                  <span className="status-name">
                    {status === 'draft' ? '已儲存' : 
                     status === 'pending' ? '待批准' : 
                     status === 'approved' ? '已批准' : status}
                  </span>
                </div>
                <div className="status-count">{count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="department-distribution">
          <h4>部門分布</h4>
          <div className="department-stats">
            {Object.entries(stats.departmentStats || {}).map(([department, count]) => (
              <div key={department} className="department-stat-item">
                <div className="department-info">
                  <span className="department-icon">{getDepartmentIcon(department)}</span>
                  <span className="department-name">{department}</span>
                </div>
                <div className="department-count">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="leave-type-distribution">
        <h4>請假類型分布</h4>
        <div className="leave-type-stats">
          {Object.entries(stats.leaveTypeStats || {}).map(([leaveType, count]) => (
            <div key={leaveType} className="leave-type-stat-item">
              <div className="leave-type-info">
                <span className="leave-type-icon">{getLeaveTypeIcon(leaveType)}</span>
                <span className="leave-type-name">{leaveType}</span>
              </div>
              <div className="leave-type-count">{count}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="monthly-record-chart">
        <h4>月度記錄趨勢</h4>
        <div className="chart-container">
          <div className="chart-bars">
            {monthlyData.map(({ month, count }, index) => (
              <div key={index} className="chart-bar">
                <div 
                  className="bar-fill"
                  style={{ 
                    height: `${(count / maxMonthlyCount) * 100}%`,
                    backgroundColor: count > 0 ? '#3b82f6' : '#e5e7eb'
                  }}
                ></div>
                <div className="bar-label">{month}</div>
                <div className="bar-value">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="system-info">
        <div className="info-item">
          <span className="info-label">系統狀態:</span>
          <span className="info-value status-active">正常運行</span>
        </div>
        <div className="info-item">
          <span className="info-label">最後更新:</span>
          <span className="info-value">{new Date().toLocaleString('zh-TW')}</span>
        </div>
        <div className="info-item">
          <span className="info-label">數據來源:</span>
          <span className="info-value">Supabase + Mock Data</span>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;
